/**
 * VillageSystem: the game-side glue for villagers, iron golems and pillagers.
 * main.ts builds one and calls (one line per hook):
 *   tick()                   after entities.tick()
 *   interact(mob, held)      right-click on a mob (trade / feed / repair)
 *   onPlace(x, y, z, id)     after a block was placed (iron golem building)
 *
 * It also: lets zombies hunt villagers (wrapping their AI), turns villagers
 * killed by zombies into zombie villagers (normal 50%, hard 100%), summons a
 * golem for scared villagers, breeds villagers (food + free beds), populates
 * generated villages and pillager outposts once (flag stored in the save) and
 * spawns night-time pillager patrols.
 *
 * Bad Omen / raids are out of scope (no status-effect system).
 */

import { AIR, BED_HEAD_ID, BLOCKS, collisionOf, PUMPKIN_ID, SNOW_BLOCK_ID } from '../../blocks';
import { isSpawnEgg } from '../../game/spawnEggs';
import { I } from '../../game/itemIds';
import { playerTradeXp, resultOf, satisfies, takePayment, useOffer } from '../../game/trading';
import type { Stack } from '../../game/types';
import { mulberry32 } from '../../noise';
import { ChunkState } from '../../world/chunk';
import type { EntityHost } from '../host';
import type { EntityManager } from '../manager';
import type { Mob, MobCtx } from '../mob';
import { claims, deathHooks, hurtMob, tickPendingHits, vh, type Difficulty } from './ctx';
import { IronGolem } from './golem';
import { populateStructureMobs, snowGolemPattern } from '../mobs3';
import { Pillager, ZombieVillager } from './illagers';
import { BREED_FOOD_NEEDED, Villager, VILLAGER_FOOD, villagerHooks } from './villager';

const JACK_O_LANTERN = 'Jack o\'Lantern';

export interface VillageDeps {
  entities: EntityManager;
  host: EntityHost;
  setBlock(x: number, y: number, z: number, id: number): void;
  /** Debug-HUD structure finder (TerrainGenerator.nearestStructures). */
  structures(x: number, z: number): readonly { name: string; x: number; z: number; y: number; dist: number }[];
  biomeName(x: number, z: number): string;
  /** Persistent world flags (villages / outposts populated). */
  flag: { has(key: string): boolean; set(key: string): void };
  sound(id: string, x: number, y: number, z: number): void;
  toss(vy: number): void;
  dimension(): string;
  creative(): boolean;
  consumeHeld(n: number): void;
  /** Open the trade screen for villager `v`; `onClose` runs when it closes. */
  openTrade(v: Villager, onClose: () => void): void;
  difficulty?(): Difficulty;
  /** Shared clock in ticks (a server with one system per dimension); absent: the system counts its own. */
  now?(): number;
}

/** Pick the vanilla villager biome type index from a biome name. */
export function villagerTypeFor(biome: string): number {
  const b = biome.toLowerCase();
  if (b.includes('desert') || b.includes('badlands')) return 1;
  if (b.includes('savanna')) return 2;
  if (b.includes('snow') || b.includes('frozen') || b.includes('ice') || b.includes('grove') || b.includes('peaks')) return 4;
  if (b.includes('taiga')) return 3;
  if (b.includes('jungle')) return 5;
  if (b.includes('swamp')) return 6;
  return 0;
}

/** Zombie kill conversion chance (vanilla: easy 0, normal 50%, hard 100%). */
export const conversionChance = (d: Difficulty): number => (d === 'hard' ? 1 : d === 'normal' ? 0.5 : 0);

/** T-shape of iron blocks under a carved pumpkin. Returns the arm axis or null. */
export function golemPattern(get: (x: number, y: number, z: number) => number, x: number, y: number, z: number, ironId: number): 'x' | 'z' | null {
  if (get(x, y - 1, z) !== ironId || get(x, y - 2, z) !== ironId) return null;
  for (const ax of ['x', 'z'] as const) {
    const dx = ax === 'x' ? 1 : 0, dz = ax === 'z' ? 1 : 0;
    if (get(x + dx, y - 1, z + dz) === ironId && get(x - dx, y - 1, z - dz) === ironId
      && collisionOf(get(x + dx, y - 2, z + dz)).length === 0 && collisionOf(get(x - dx, y - 2, z - dz)).length === 0) return ax;
  }
  return null;
}

export class VillageSystem {
  private readonly ironId: number;
  private readonly jackId: number;
  private readonly patched = new WeakSet<Mob>();
  private readonly zTarget = new WeakMap<Mob, { t: Mob | null; cd: number }>();
  private readonly known = new Set<Villager>();
  private golemCooldown = new Map<string, number>();
  private scanTimer = 0;
  private patrolTimer = 12000;
  private t = 0;

  constructor(private readonly d: VillageDeps) {
    this.ironId = findBlockId('Block of Iron');
    this.jackId = findBlockId(JACK_O_LANTERN);
    this.bind();
  }

  /** Point the module-level village hooks at this system (a server re-binds per dimension). */
  bind(): void {
    const d = this.d;
    vh.host = d.host;
    vh.mobs = () => d.entities.mobs;
    vh.spawn = (t, x, y, z) => d.entities.spawnMob(t, x, y, z);
    vh.sound = (id, x, y, z) => d.sound(id, x, y, z);
    vh.toss = (vy) => d.toss(vy);
    vh.difficulty = () => d.difficulty?.() ?? 'normal';
    villagerHooks.scared = (v) => this.summonGolem(v);
    villagerHooks.angerGolems = (x, y, z) => {
      for (const m of d.entities.mobs) {
        if (!(m instanceof IronGolem) || !m.alive || m.playerBuilt) continue;
        if ((m.x - x) ** 2 + (m.y - y) ** 2 + (m.z - z) ** 2 < 16 * 16) m.provoke(400 + Math.floor(Math.random() * 380));
      }
    };
    deathHooks.length = 0;
    deathHooks.push((victim, killer) => this.convert(victim, killer));
  }

  // ------------------------------------------------------------------ hooks

  tick(): void {
    this.t = this.d.now ? this.d.now() : this.t + 1;
    vh.tick = this.t;
    tickPendingHits();
    const mobs = this.d.entities.mobs;
    for (const m of mobs) {
      if (m.type === 'zombie' && !this.patched.has(m)) this.patchZombie(m);
      if (m instanceof Villager) this.known.add(m);
    }
    for (const v of this.known) {
      if (v.removed || !v.alive) { v.releaseClaims(); this.known.delete(v); }
    }
    if (this.t % 100 === 0) this.breeding();
    if (this.d.dimension() !== 'overworld') return;
    if (++this.scanTimer >= 100) { this.scanTimer = 0; this.populate(); }
    this.patrols();
  }

  /** Right-click on `mob` holding `held`. True = handled (swing the arm). */
  interact(mob: Mob, held: Stack): boolean {
    if (mob instanceof IronGolem) {
      if (held.id === I.IRON_INGOT && held.count > 0 && mob.repair()) {
        if (!this.d.creative()) this.d.consumeHeld(1);
        return true;
      }
      return false;
    }
    if (!(mob instanceof Villager) || !mob.alive) return false;
    const food = VILLAGER_FOOD.get(held.id);
    if (food && held.count > 0 && !mob.baby) {
      mob.food += food;
      if (!this.d.creative()) this.d.consumeHeld(1);
      this.d.host.effect('heart', mob.x, mob.y + mob.height + 0.3, mob.z);
      return true;
    }
    if (isSpawnEgg(held.id)) return false;
    if (!mob.canTrade) {
      mob.shake = 40;
      this.d.sound('entity.villager.no', mob.x, mob.y + 1, mob.z);
      return true;
    }
    mob.trading = true;
    this.d.sound('entity.villager.trade', mob.x, mob.y + 1, mob.z);
    this.d.openTrade(mob, () => mob.onTradeClosed(this.d.host.rnd));
    return true;
  }

  /** Block placed: a pumpkin / jack o'lantern on an iron T builds a golem. */
  onPlace(x: number, y: number, z: number, id: number): void {
    if (id !== PUMPKIN_ID && id !== this.jackId) return;
    const w = this.d.host.world;
    const ax = golemPattern((a, b, c) => w.getBlock(a, b, c), x, y, z, this.ironId);
    if (!ax) {
      // two snow blocks under the pumpkin: a snow golem
      if (snowGolemPattern((a, b, c) => w.getBlock(a, b, c), x, y, z, SNOW_BLOCK_ID)) {
        for (let k = 0; k < 3; k++) this.d.setBlock(x, y - k, z, AIR);
        if (this.d.entities.spawnMob('snow_golem', x + 0.5, y - 2, z + 0.5)) this.d.host.effect('poof', x + 0.5, y - 1, z + 0.5);
      }
      return;
    }
    const dx = ax === 'x' ? 1 : 0, dz = ax === 'z' ? 1 : 0;
    for (const [a, b, c] of [[x, y, z], [x, y - 1, z], [x, y - 2, z], [x + dx, y - 1, z + dz], [x - dx, y - 1, z - dz]]) {
      this.d.setBlock(a, b, c, AIR);
    }
    const g = this.d.entities.spawnMob('iron_golem', x + 0.5, y - 2, z + 0.5);
    if (g instanceof IronGolem) { g.playerBuilt = true; this.d.host.effect('poof', x + 0.5, y - 1, z + 0.5); }
  }

  // ------------------------------------------------------------------ trading

  /** Perform one trade of `offerIdx` using the payment slots; returns the result stack or null. */
  static trade(v: Villager, offerIdx: number, pa: Stack, pb: Stack, rnd: () => number, dropXp: (n: number) => void): Stack | null {
    const o = v.offers[offerIdx];
    if (!o || !satisfies(o, pa, pb)) return null;
    takePayment(o, pa, pb);
    v.xp += useOffer(o);
    dropXp(playerTradeXp(rnd));
    return resultOf(o);
  }

  // ------------------------------------------------------------------ zombies

  private patchZombie(z: Mob): void {
    this.patched.add(z);
    const zz = z as unknown as { ai(ctx: MobCtx): void; setMove(dx: number, dz: number, m: number): void; faceToward(x: number, z: number): void; lookAt(x: number, y: number, z: number): void; swing(): void };
    const orig = zz.ai.bind(z);
    zz.ai = (ctx: MobCtx) => {
      orig(ctx);
      if (z.aggressive || !z.alive) return;
      let st = this.zTarget.get(z);
      if (!st) { st = { t: null, cd: 0 }; this.zTarget.set(z, st); }
      if (st.cd > 0) st.cd--;
      if (!st.t || !st.t.alive || (ctx.tick + z.id) % 20 === 0) {
        let best: Mob | null = null, bd = 16 * 16;
        for (const m of this.d.entities.mobs) {
          if (!m.alive || (m.type !== 'villager' && m.type !== 'iron_golem')) continue;
          const d2 = (m.x - z.x) ** 2 + (m.y - z.y) ** 2 + (m.z - z.z) ** 2;
          if (d2 < bd) { bd = d2; best = m; }
        }
        st.t = best;
      }
      const t = st.t;
      if (!t) return;
      z.aggressive = true;
      zz.setMove(t.x - z.x, t.z - z.z, 1);
      zz.faceToward(t.x, t.z);
      zz.lookAt(t.x, t.eyeY, t.z);
      const reach = z.width / 2 + t.width / 2 + 0.9;
      if (st.cd === 0 && (t.x - z.x) ** 2 + (t.z - z.z) ** 2 < reach * reach && Math.abs(t.y - z.y) < 1.5) {
        st.cd = 20;
        zz.swing();
        hurtMob(z, t, 3, 0.4);
      }
    };
  }

  private convert(victim: Mob, killer: Mob): boolean {
    if (!(victim instanceof Villager) || (killer.type !== 'zombie' && killer.type !== 'zombie_villager')) return false;
    if (this.d.host.rnd() >= conversionChance(vh.difficulty())) return false;
    victim.removed = true;
    const z = this.d.entities.spawnMob('zombie_villager', victim.x, victim.y, victim.z);
    if (z instanceof ZombieVillager) { z.vtype = victim.vtype; z.profession = victim.profession; z.yaw = victim.yaw; }
    this.d.sound('entity.zombie_villager.converted', victim.x, victim.y + 1, victim.z);
    return true;
  }

  // ------------------------------------------------------------------ golems & breeding

  private summonGolem(v: Villager): void {
    const cell = `${Math.floor(v.x / 32)},${Math.floor(v.z / 32)}`;
    if ((this.golemCooldown.get(cell) ?? -Infinity) > this.t) return;
    let villagers = 0;
    for (const m of this.d.entities.mobs) {
      if (!m.alive) continue;
      const d2 = (m.x - v.x) ** 2 + (m.z - v.z) ** 2;
      if (m.type === 'iron_golem' && d2 < 16 * 16) return;
      if (m.type === 'villager' && d2 < 10 * 10) villagers++;
    }
    if (villagers < 3) return;
    this.golemCooldown.set(cell, this.t + 12000);
    const p = this.surfaceSpot(v.x, v.y, v.z, 8, this.d.host.rnd);
    if (p) this.d.entities.spawnMob('iron_golem', p[0], p[1], p[2]);
  }

  private breeding(): void {
    const mobs = this.d.entities.mobs;
    const vs = mobs.filter((m): m is Villager => m instanceof Villager && m.alive);
    // farmers feed the village: +1 food to every villager near a farmer each 2400 ticks
    if (this.t % 2400 === 0) {
      for (const v of vs) if (vs.some((f) => f.profession === 'farmer' && (f.x - v.x) ** 2 + (f.z - v.z) ** 2 < 48 * 48)) v.food = Math.min(64, v.food + 4);
    }
    for (const a of vs) {
      if (a.baby || a.breedCooldown > 0 || a.food < BREED_FOOD_NEEDED || a.sleeping) continue;
      const b = vs.find((o) => o !== a && !o.baby && o.breedCooldown <= 0 && o.food >= BREED_FOOD_NEEDED && (o.x - a.x) ** 2 + (o.z - a.z) ** 2 < 64);
      if (!b) continue;
      const near = vs.filter((o) => (o.x - a.x) ** 2 + (o.z - a.z) ** 2 < 32 * 32).length;
      if (countBlocks(this.d.host.world, a.x, a.y, a.z, 32, 6, BED_HEAD_ID) <= near) continue;
      a.food -= BREED_FOOD_NEEDED; b.food -= BREED_FOOD_NEEDED;
      a.breedCooldown = b.breedCooldown = 6000;
      const baby = this.d.entities.spawnMob('villager', (a.x + b.x) / 2, a.y, (a.z + b.z) / 2);
      if (baby instanceof Villager) { baby.setBaby(true); baby.vtype = a.vtype; }
      this.d.host.effect('heart', a.x, a.y + 2, a.z);
      this.d.host.effect('heart', b.x, b.y + 2, b.z);
    }
  }

  // ------------------------------------------------------------------ natural spawning

  private chunksReady(x: number, z: number, r: number): boolean {
    const w = this.d.host.world;
    for (let cz = Math.floor((z - r) / 16); cz <= Math.floor((z + r) / 16); cz++) {
      for (let cx = Math.floor((x - r) / 16); cx <= Math.floor((x + r) / 16); cx++) {
        const ch = w.getChunk(cx, cz);
        if (!ch || ch.state !== ChunkState.Ready) return false;
      }
    }
    return true;
  }

  private populate(): void {
    const p = this.d.host.player;
    for (const s of this.d.structures(p.x, p.z)) {
      if (s.dist > 96) continue;
      if (s.name === 'Ocean Monument' || s.name === 'Woodland Mansion') { this.structureMobs(s); continue; }
      const key = `${s.name === 'Village' ? 'village' : 'outpost'}:${Math.round(s.x)},${Math.round(s.z)}`;
      if (s.name === 'Pillager Outpost') this.outpostTopUp(s);
      if (s.name !== 'Village' && s.name !== 'Pillager Outpost') continue;
      if (this.d.flag.has(key)) continue;
      if (!this.chunksReady(s.x, s.z, s.name === 'Village' ? 56 : 12)) continue;
      this.d.flag.set(key);
      const rnd = mulberry32((Math.round(s.x) * 73856093) ^ (Math.round(s.z) * 19349663));
      if (s.name === 'Village') this.spawnVillagers(s.x, s.y, s.z, rnd);
      else this.spawnOutpost(s.x, s.y, s.z, rnd);
    }
  }

  /** Monument guardians and mansion illagers (entities/mobs3). */
  private structureMobs(s: { name: string; x: number; y: number; z: number }): void {
    const e = this.d.entities;
    populateStructureMobs({
      world: this.d.host.world, spawn: (t, x, y, z) => e.spawnMob(t, x, y, z), mobs: () => e.mobs, flag: this.d.flag,
      rnd: this.d.host.rnd, ready: (x, z, r) => this.chunksReady(x, z, r), tick: this.t,
    }, s);
  }

  private spawnVillagers(cx: number, cy: number, cz: number, rnd: () => number): void {
    const w = this.d.host.world;
    const vtype = villagerTypeFor(this.d.biomeName(cx, cz));
    const beds: [number, number, number][] = [];
    for (let y = cy - 8; y <= cy + 14; y++) {
      for (let z = cz - 60; z <= cz + 60; z++) {
        for (let x = cx - 60; x <= cx + 60; x++) if (w.getBlock(x, y, z) === BED_HEAD_ID) beds.push([x, y, z]);
      }
    }
    const n = Math.max(3, Math.min(20, beds.length));
    for (let i = 0; i < n; i++) {
      const b = beds[i];
      const spot = b ? this.surfaceSpot(b[0] + 0.5, b[1], b[2] + 0.5, 3, rnd) : this.surfaceSpot(cx + 0.5, cy + 1, cz + 0.5, 6, rnd);
      if (!spot) continue;
      const v = this.d.entities.spawnMob('villager', spot[0], spot[1], spot[2]);
      if (!(v instanceof Villager)) continue;
      v.vtype = vtype;
      v.meet = [Math.floor(cx), Math.floor(cy), Math.floor(cz)];
      if (rnd() < 1 / 16) v.setProfession('nitwit', rnd);
      if (rnd() < 0.15) v.setBaby(true);
    }
    const g = this.surfaceSpot(cx + 0.5, cy + 1, cz + 0.5, 8, rnd);
    if (g) this.d.entities.spawnMob('iron_golem', g[0], g[1], g[2]);
    // stray cats: one per four beds, at most five (vanilla CatSpawner's village rule)
    const cats = Math.max(1, Math.min(5, Math.floor(beds.length / 4)));
    for (let i = 0; i < cats; i++) {
      const b = beds[Math.floor(rnd() * beds.length)];
      const spot = b ? this.surfaceSpot(b[0] + 0.5, b[1], b[2] + 0.5, 6, rnd) : this.surfaceSpot(cx + 0.5, cy + 1, cz + 0.5, 10, rnd);
      if (spot) this.d.entities.spawnMob('cat', spot[0], spot[1], spot[2]);
    }
  }

  private spawnOutpost(cx: number, cy: number, cz: number, rnd: () => number): void {
    // 2-4 around the base, one on the lookout deck (deck floor at y + 16)
    const n = 2 + Math.floor(rnd() * 3);
    for (let i = 0; i < n; i++) {
      const s = this.surfaceSpot(cx + 0.5, cy + 1, cz + 0.5, 9, rnd);
      if (s) this.spawnPillager(s[0], s[1], s[2], false);
    }
    this.spawnPillager(cx + 0.5, cy + 17, cz + 0.5, false);
  }

  private outpostTopUp(s: { x: number; y: number; z: number; dist: number }): void {
    if (this.t % 600 !== 0 || s.dist < 24) return;
    let n = 0;
    for (const m of this.d.entities.mobs) if (m.type === 'pillager' && m.alive && (m.x - s.x) ** 2 + (m.z - s.z) ** 2 < 32 * 32) n++;
    if (n >= 4 || !this.chunksReady(s.x, s.z, 12)) return;
    const p = this.surfaceSpot(s.x + 0.5, s.y + 1, s.z + 0.5, 9, this.d.host.rnd);
    if (p) this.spawnPillager(p[0], p[1], p[2], false);
  }

  private spawnPillager(x: number, y: number, z: number, captain: boolean, persistent = true): void {
    const m = this.d.entities.spawnMob('pillager', x, y, z);
    if (m instanceof Pillager) { m.captain = captain; m.persistent = persistent; }
  }

  /** Vanilla PatrolSpawner, simplified: every 10-20 min, 20%, at night, 24-48 blocks out, 2-5 pillagers with a captain. */
  private patrols(): void {
    if (--this.patrolTimer > 0) return;
    const rnd = this.d.host.rnd;
    this.patrolTimer = 12000 + Math.floor(rnd() * 12000);
    if (!this.d.host.isNight() || rnd() >= 0.2) return;
    const p = this.d.host.player;
    const a = rnd() * Math.PI * 2, r = 24 + rnd() * 24;
    const x = p.x + Math.cos(a) * r, z = p.z + Math.sin(a) * r;
    if (!this.chunksReady(x, z, 4)) return;
    const n = 2 + Math.floor(rnd() * 4);
    for (let i = 0; i < n; i++) {
      const s = this.surfaceSpot(x, p.y + 10, z, 4, rnd, 40);
      if (s) this.spawnPillager(s[0], s[1], s[2], i === 0, false);
    }
  }

  /** A random standable spot within `r` of (x, z), searching `dy` up/down from y. */
  private surfaceSpot(x: number, y: number, z: number, r: number, rnd: () => number, dy = 6): [number, number, number] | null {
    const w = this.d.host.world;
    for (let tries = 0; tries < 16; tries++) {
      const bx = Math.floor(x + (rnd() * 2 - 1) * r), bz = Math.floor(z + (rnd() * 2 - 1) * r);
      for (let k = 0; k <= dy * 2; k++) {
        const by = Math.floor(y) + (k % 2 ? (k + 1) / 2 : -k / 2);
        if (collisionOf(w.getBlock(bx, by, bz)).length || collisionOf(w.getBlock(bx, by + 1, bz)).length) continue;
        const below = w.getBlock(bx, by - 1, bz);
        if (collisionOf(below).length === 0) continue;
        return [bx + 0.5, by, bz + 0.5];
      }
    }
    return null;
  }

  /** For the smoke test / debug: current claims count. */
  static claimCount(): number { return claims.size; }
}

let byName: Map<string, number> | null = null;
function findBlockId(name: string): number {
  if (!byName) { byName = new Map(); for (const b of BLOCKS) if (b && !byName.has(b.name)) byName.set(b.name, b.id); }
  return byName.get(name) ?? 0;
}

function countBlocks(w: { getBlock(x: number, y: number, z: number): number }, x: number, y: number, z: number, r: number, ry: number, id: number): number {
  let n = 0;
  const bx = Math.floor(x), by = Math.floor(y), bz = Math.floor(z);
  for (let dy = -ry; dy <= ry; dy++) for (let dz = -r; dz <= r; dz++) for (let dx = -r; dx <= r; dx++) if (w.getBlock(bx + dx, by + dy, bz + dz) === id) n++;
  return n;
}

